// src/frontend/src/api/index.js
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

const client = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Auth token management
let authToken = null;

const setAuthToken = (token) => {
  authToken = token;
  if (token) {
    client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete client.defaults.headers.common['Authorization'];
  }
};

// Response interceptor for error handling
client.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message = error.response?.data?.error || error.response?.data?.message || error.message;

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(new Error(message));
  }
);

// Auth API
const auth = {
  login: (email, password) =>
    client.post('/auth/login', { email, password }),

  refresh: (refreshToken) =>
    client.post('/auth/refresh', { refreshToken }),

  me: () =>
    client.get('/auth/me'),

  changePassword: (currentPassword, newPassword) =>
    client.put('/auth/password', { currentPassword, newPassword }),

  logout: () =>
    client.post('/auth/logout')
};

// Documents API
const documents = {
  list: (params) =>
    client.get('/documents', { params }),

  get: (id) =>
    client.get(`/documents/${id}`),

  create: (formData) =>
    client.post('/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  update: (id, data) =>
    client.put(`/documents/${id}`, data),

  updateStatus: (id, status) =>
    client.patch(`/documents/${id}/status`, { status }),

  delete: (id) =>
    client.delete(`/documents/${id}`),

  getPdf: (id) =>
    client.get(`/documents/${id}/pdf`),

  getMarkdown: (id) =>
    client.get(`/documents/${id}/markdown`),

  uploadPdf: (id, formData) =>
    client.post(`/documents/${id}/pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Duplicate document with different policy
  getDuplicateOptions: (id) =>
    client.get(`/documents/${id}/duplicate-options`),

  duplicate: (id, policy) =>
    client.post(`/documents/${id}/duplicate`, { policy }),

  // Revisions API
  getRevisions: (documentId, params) =>
    client.get(`/documents/${documentId}/revisions`, { params }),

  getRevision: (documentId, revisionNumber) =>
    client.get(`/documents/${documentId}/revisions/${revisionNumber}`),

  compareRevisions: (documentId, from, to) =>
    client.get(`/documents/${documentId}/revisions/compare`, { params: { from, to } }),

  revertToRevision: (documentId, revisionNumber, comment) =>
    client.post(`/documents/${documentId}/revisions/${revisionNumber}/revert`, { comment })
};

// Graphs API
const graphs = {
  list: () =>
    client.get('/graphs'),

  get: (id) =>
    client.get(`/graphs/${id}`),

  validate: (id, path) =>
    client.post(`/graphs/${id}/validate`, { path })
};

// Admin API
const admin = {
  // Users
  listUsers: () =>
    client.get('/admin/users'),

  createUser: (data) =>
    client.post('/admin/users', data),

  updateUser: (id, data) =>
    client.put(`/admin/users/${id}`, data),

  deleteUser: (id) =>
    client.delete(`/admin/users/${id}`),

  // Document creation (without PDF)
  createDocument: (data) =>
    client.post('/admin/documents', data),

  bulkCreateDocuments: (documents, conflictResolution = 'skip') =>
    client.post('/admin/documents/bulk', { documents, conflictResolution }),

  // Import/Export
  import: (formData) =>
    client.post('/admin/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  importPdfs: (formData) =>
    client.post('/admin/import-pdfs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  export: (params) =>
    client.get('/admin/export', {
      params,
      responseType: 'blob'
    }),

  getTemplates: (section) =>
    client.get('/admin/export/template', {
      params: { section },
      responseType: 'blob'
    }),

  deleteAllDocuments: () =>
    client.delete('/admin/documents/all')
};

// ═══════════════════════════════════════════════════════════════════════════
// SPLIT MODEL (ticket 0055)
// ═══════════════════════════════════════════════════════════════════════════

// The shared layer: identity, files and the shared DAS.
const articles = {
  list: (params = {}) => client.get('/articles', { params }),

  get: (id) => client.get(`/articles/${id}`),

  create: (data) => client.post('/articles', data),

  /** Distinct journal and cohort values, for the dashboard's filters. */
  facets: () => client.get('/articles/facets'),

  update: (id, data) => client.put(`/articles/${id}`, data),

  /** `cascade` also removes the article's OSI and Snapshot records. */
  remove: (id, { cascade = false } = {}) =>
    client.delete(`/articles/${id}${cascade ? '?cascade=true' : ''}`),

  /**
   * Ask how many times a DOI has already been processed.
   * A DOI is not unique, so this drives the "attach or create new" prompt
   * rather than blocking the write.
   */
  checkDoi: (doi) => client.get('/articles/check-doi', { params: { doi } }),

  // ── Files ────────────────────────────────────────────────────────────────
  // Files belong to the article, so both curation sides reach the same ones.
  files: (id) => client.get(`/articles/${id}/files`),

  /** A short-lived presigned URL. Not cached: it expires. */
  fileUrl: (id, kind) => client.get(`/articles/${id}/files/${kind}`),

  uploadFile: (id, kind, file) => {
    const form = new FormData();
    form.append('file', file);
    return client.post(`/articles/${id}/files/${kind}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  /**
   * Delete several at once. `cascade` takes their OSI and Snapshot passes with
   * them; without it the API refuses the whole batch if any carries curation.
   */
  removeMany: (ids, { cascade = false } = {}) =>
    client.delete('/articles/bulk', { data: { ids, cascade } }),

  removeFile: (id, kind) => client.delete(`/articles/${id}/files/${kind}`),

  markdown: (id) => client.get(`/articles/${id}/markdown`),

  // ── History ──────────────────────────────────────────────────────────────
  revisions: (id, params = {}) => client.get(`/articles/${id}/revisions`, { params }),

  revision: (id, revisionNumber) => client.get(`/articles/${id}/revisions/${revisionNumber}`),

  revert: (id, revisionNumber) => client.post(`/articles/${id}/revisions/${revisionNumber}/revert`)
};

/**
 * Both curation sides expose the same endpoints, so one factory serves both.
 * @param {'osi'|'snapshot'} side
 */
const buildRecordsApi = (side) => ({
  list: (params = {}) => client.get(`/${side}`, { params }),

  get: (id) => client.get(`/${side}/${id}`),

  create: (data) => client.post(`/${side}`, data),

  update: (id, data) => client.put(`/${side}/${id}`, data),

  /** Merge field values into the curated blob without replacing it. */
  patchData: (id, data, section) => client.patch(`/${side}/${id}/data`, { data, section }),

  /** Copy the article's shared DAS onto this record - always explicit. */
  copySharedDas: (id) => client.post(`/${side}/${id}/copy-shared-das`),

  remove: (id) => client.delete(`/${side}/${id}`),

  /** Delete several records, reporting what happened to each. */
  removeMany: (ids) => client.delete(`/${side}/bulk`, { data: { ids } }),

  revisions: (id, params = {}) => client.get(`/${side}/${id}/revisions`, { params }),

  revision: (id, revisionNumber) => client.get(`/${side}/${id}/revisions/${revisionNumber}`),

  revert: (id, revisionNumber) => client.post(`/${side}/${id}/revisions/${revisionNumber}/revert`)
});

const osi = buildRecordsApi('osi');
const snapshot = buildRecordsApi('snapshot');

// Bulk file import, matched to articles by DOI.
const files = {
  importArchive: (kind, file, dryRun = false) => {
    const form = new FormData();
    form.append('file', file);
    return client.post(`/exports/files/archive/${kind}?dry_run=${dryRun}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  /**
   * Sent as a file rather than a JSON body: the corpus export runs to ~100MB,
   * well past the API's JSON body limit.
   */
  importMarkdown: (file, dryRun = false) => {
    const form = new FormData();
    form.append('file', file);
    return client.post(`/exports/files/markdown?dry_run=${dryRun}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  reconcile: (dryRun = false) => client.post('/exports/files/reconcile', { dryRun })
};

/**
 * Notes on a curated entity and its fields (ticket 0063).
 * `entityType` is one of article | osi_record | snapshot_record.
 */
const notes = {
  list: (entityType, entityId, params = {}) =>
    client.get(`/notes/${entityType}/${entityId}`, { params }),

  create: (entityType, entityId, body, fieldKey = null) =>
    client.post(`/notes/${entityType}/${entityId}`, { body, fieldKey }),

  update: (id, body) => client.put(`/notes/${id}`, { body }),

  setResolved: (id, resolved) => client.patch(`/notes/${id}/resolved`, { resolved }),

  remove: (id) => client.delete(`/notes/${id}`)
};

/**
 * The field definitions the backend renders forms and exports from.
 *
 * Fetched rather than duplicated in the frontend: the comparison view walks the
 * same field list the export does, so a field added on one side cannot go
 * missing on the other.
 */
const schemas = {
  groups: (key) => client.get(`/schemas/${key}/groups`)
};

/**
 * The caller's own activity (ticket 0085).
 *
 * No user parameter, deliberately - the endpoint takes the caller from their
 * session and there is no way to ask for anyone else's trail.
 */
const activity = {
  trail: ({ days, includeImports, limit } = {}) => {
    const params = new URLSearchParams()
    if (days) {params.set('days', String(days))}
    if (includeImports) {params.set('include_imports', 'true')}
    if (limit) {params.set('limit', String(limit))}
    const query = params.toString()
    return client.get(`/activity${query ? `?${query}` : ''}`)
  },

  changes: ({ days, includeImports, limit } = {}) => {
    const params = new URLSearchParams()
    if (days) {params.set('days', String(days))}
    if (includeImports) {params.set('include_imports', 'true')}
    if (limit) {params.set('limit', String(limit))}
    const query = params.toString()
    return client.get(`/activity/changes${query ? `?${query}` : ''}`)
  }
};

const exports_ = {
  /** Browser download URL for an export mode: osi | snapshot | both | workbook. */
  url: (mode) => `${API_BASE}/exports/${mode}`,

  previewImport: (csv) => client.post('/exports/osi/preview-import', { csv })
};

export default {
  client,
  setAuthToken,
  auth,
  documents,
  graphs,
  admin,
  articles,
  files,
  osi,
  snapshot,
  notes,
  schemas,
  activity,
  exports: exports_
};
